/**
 * ENG-10351 - do not rotate a working session onto a host that cannot start a
 * new one.
 *
 * THE INCIDENT. On 2026-09-10 the day rollover on agt-aws-1 tore down live
 * sessions on a host whose `~/.claude.json` was not usable. Every respawn landed
 * on Claude Code's login picker. marlow fell first (17:41:57Z), phil at
 * 20:01:03Z, and the other six agents on the host were queued to follow as their
 * own rollovers fired. The host gap was discovered one agent at a time, by each
 * agent going down.
 *
 * ENG-10335 made a session parked on the picker read as blocked. That is
 * detection. This module is prevention: before a PLANNED rotation (day
 * rollover) tears down a session that is working, the manager asks whether the
 * file the respawn needs is there. If it is not, the rotation waits, the working
 * session keeps working, and the heartbeat carries ONE host-level verdict so the
 * control plane raises one alert instead of one per agent.
 *
 * WHAT "USABLE" MEANS, AND WHY NOT MORE. Present, readable, parses as JSON, and
 * is a non-empty object. That covers both known routes to the picker: the file
 * is absent (ENG-4633, the 2026-05-01 scout outage) or corrupted (ENG-10140,
 * the shared `/root/.claude.json` on agt-aws-1). It deliberately does not
 * require a specific key such as `hasCompletedOnboarding`: the pair flow in
 * claude-pair-runtime.ts declines to do that for the stated reason that Claude
 * Code's write timing and key names are version-dependent, and a gate that
 * guesses wrong would block every rollover on a healthy host forever. A false
 * "usable" costs what happened before this change; a false "unusable" costs a
 * fleet that never rotates. The bar is set against the second.
 *
 * CREDENTIAL GUARDRAIL. This file is Claude Code's global config and can hold
 * account identifiers and MCP server env. The contents are parsed in memory to
 * test the shape and discarded. Nothing from the file, and no parser error
 * message (V8's JSON.parse errors quote a slice of the input), is returned,
 * logged or sent: a verdict is one of a fixed set of reason codes.
 * `.credentials.json` is never touched.
 */
import { readFileSync, statSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { effectiveClaudeJsonPath } from './claude-config-location.js';

export const CLAUDE_DEVICE_STATE_REASONS = [
  'missing',
  'not-a-file',
  'unreadable',
  'unparseable',
  'not-an-object',
  'empty-object',
] as const;
export type ClaudeDeviceStateReason = (typeof CLAUDE_DEVICE_STATE_REASONS)[number];

export type ClaudeDeviceStateVerdict =
  | { usable: true }
  | { usable: false; reason: ClaudeDeviceStateReason };

/**
 * The `.claude.json` a respawn will read.
 *
 * ENG-10869: delegated to the shared locator rather than recomputed here. The
 * comment this replaces said it "mirrors the spawn path" and warned that
 * "checking any other path would let the gate pass while the respawn fails" -
 * which was exactly right, and is precisely why mirroring by duplication was
 * the wrong mechanism.
 *
 * `effectiveClaudeJsonPath`, NOT the unconditional config-dir path. The spawn
 * is CONDITIONAL — a container is pointed at `<configDir>/.claude.json` only
 * once that file is usable, and otherwise reads the legacy one — so resolving
 * the config-dir path here breaks the gate in both directions, and a reviewer
 * caught both:
 *
 *  - FALSE-UNUSABLE, which is the expensive one. On any host that has not
 *    migrated, this would read a path where nothing exists and report
 *    `missing`. `deviceStateAllowsRollover` then holds every day rollover
 *    indefinitely, `selfUpdateDeviceStateHold` refuses every CLI upgrade
 *    including an urgent hotfix, and `heartbeatField` emits the host-wide
 *    critical alert every 30s — on a host whose agents are respawning happily
 *    from the legacy file. ENG-10730's own docblock sets the bar against
 *    exactly this: "a false 'unusable' costs a fleet that never rotates", and
 *    it deliberately declined to require `hasCompletedOnboarding` for that
 *    reason. Resolving the config-dir path here would have made it require it
 *    transitively, through this module's own readiness rule.
 *  - FALSE-USABLE. On a relocated config dir the gate would read the seeded
 *    file and say ready while the container reads the legacy path — the
 *    gate-passes-while-the-respawn-reads-another-file failure this function
 *    exists to prevent.
 */
export function claudeDeviceStatePath(env: NodeJS.ProcessEnv = process.env): string {
  return effectiveClaudeJsonPath(undefined, env);
}

/**
 * ENG-10730: the CHEAP half of the verdict - does a file exist at the path at
 * all. `statSync` only: no `readFileSync`, no `JSON.parse`, not one byte of the
 * file read.
 *
 * WHY A SECOND, WEAKER CHECK EXISTS. `heartbeatField` needs to ask "is the file
 * gone?" on every heartbeat of every host, including healthy ones. Asking the
 * full `checkClaudeDeviceState` there would put a multi-MB synchronous
 * `JSON.parse` on the manager's event loop every 30s on hosts where nothing is
 * wrong - the ENG-8079 starvation class, and the objection that shaped PR #6250.
 * This answers the only question the heartbeat needs, for one syscall.
 *
 * IT ANSWERS LESS, ON PURPOSE. A file that is present but corrupt reads
 * `present: true` here and `usable: false` from the full check. That is why a
 * hold, once begun, is re-tested with the full check and never cleared by this
 * one: a corrupt-but-present file must not look like a recovery. Use this to
 * find out that a file has GONE, never to conclude that one is well.
 *
 * The reasons it can return are a subset of `ClaudeDeviceStateReason`, so a
 * presence failure is indistinguishable downstream from the same failure found
 * the expensive way, and nothing that consumes a reason code needs to change.
 *
 * CREDENTIAL GUARDRAIL. Strengthened rather than merely preserved: this reads no
 * content at all, so there is nothing to leak. `.credentials.json` is untouched.
 */
export type ClaudeDeviceStatePresence =
  | { present: true }
  | { present: false; reason: Extract<ClaudeDeviceStateReason, 'missing' | 'not-a-file' | 'unreadable'> };

/** Never throws. Never reads a byte of the file. */
export function probeClaudeDeviceStatePresence(
  path: string = claudeDeviceStatePath(),
): ClaudeDeviceStatePresence {
  try {
    // `not-a-file` is not a theoretical branch: when the host path is unlinked
    // and a container is rebuilt, Docker creates a DIRECTORY at the bind target.
    // That is the shape ENG-10724 ended in.
    return statSync(path).isFile() ? { present: true } : { present: false, reason: 'not-a-file' };
  } catch (err) {
    return {
      present: false,
      reason: (err as NodeJS.ErrnoException)?.code === 'ENOENT' ? 'missing' : 'unreadable',
    };
  }
}

/** Never throws. Never returns, logs or echoes any byte of the file. */
export function checkClaudeDeviceState(path: string = claudeDeviceStatePath()): ClaudeDeviceStateVerdict {
  try {
    if (!statSync(path).isFile()) return { usable: false, reason: 'not-a-file' };
  } catch (err) {
    return {
      usable: false,
      reason: (err as NodeJS.ErrnoException)?.code === 'ENOENT' ? 'missing' : 'unreadable',
    };
  }
  let raw: string;
  try {
    raw = readFileSync(path, 'utf-8');
  } catch {
    return { usable: false, reason: 'unreadable' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { usable: false, reason: 'unparseable' };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { usable: false, reason: 'not-an-object' };
  }
  if (Object.keys(parsed).length === 0) return { usable: false, reason: 'empty-object' };
  return { usable: true };
}

/**
 * The heartbeat verdict. `null` is the affirmative all-clear: no rotation is
 * currently held on device state. The field is ALWAYS sent, so an absent field
 * means an older CLI and the API must change nothing (same contract as
 * `realtime_rebind_stuck`).
 */
export interface ClaudeDeviceStateRotationBlocked {
  reason: ClaudeDeviceStateReason;
  /**
   * Code names whose planned rotation is held, sorted for a stable payload.
   *
   * ENG-10730: EMPTY IS MEANINGFUL AND IS NOT A HOLD. An empty array alongside a
   * reason means the file is unusable and nothing has been stopped yet - the
   * heartbeat found it proactively, before any rotation asked. Consumers must
   * not render that as the manager deferring work, because it is not.
   * (`deferred_agents` can also carry the bracketed CLI-update sentinel rather
   * than an agent, which IS a hold - see ENG-10724.)
   */
  deferred_agents: string[];
  /**
   * When this episode began: the first rotation held, or - when nothing is held
   * - the first heartbeat that saw the file gone.
   */
  blocked_since: string;
}

/**
 * Host-wide record of rotations held on device state. One per manager process,
 * because the file is host-wide: every agent on the host reads the same one.
 */
export class DeviceStateRotationGate {
  private readonly held = new Map<string, number>();
  /**
   * ENG-10730: when the CHEAP probe first saw the file gone with nothing held.
   * Separate from `held`, which records when each agent's rotation was stopped -
   * in this state no rotation has been stopped at all. Cleared whenever the file
   * is seen present again, so a later episode gets its own clock.
   */
  private unusableSinceMs: number | null = null;

  constructor(
    private readonly check: () => ClaudeDeviceStateVerdict = () => checkClaudeDeviceState(),
    /**
     * ENG-10730: the per-heartbeat presence probe. Injected separately from
     * `check` so a test can drive the two independently and prove the asymmetry
     * above holds - in particular that a present-but-corrupt file does NOT
     * clear a hold.
     */
    private readonly probe: () => ClaudeDeviceStatePresence = () => probeClaudeDeviceStatePresence(),
  ) {}

  /**
   * Ask before tearing `codeName`'s session down for a planned rotation.
   * Returns `ready: true` when the respawn can start; otherwise the rotation must
   * be skipped this tick. `firstHold` is true only on the tick a hold begins for
   * this agent, so the caller logs once per episode rather than every poll.
   */
  mayRotate(codeName: string, nowMs: number): { ready: true } | { ready: false; reason: ClaudeDeviceStateReason; firstHold: boolean } {
    const verdict = this.check();
    if (verdict.usable) {
      // The file is back (or never went). Every hold is over, not just this
      // agent's: the file is host-wide.
      this.held.clear();
      return { ready: true };
    }
    const firstHold = !this.held.has(codeName);
    if (firstHold) this.held.set(codeName, nowMs);
    return { ready: false, reason: verdict.reason, firstHold };
  }

  /**
   * The value for the heartbeat.
   *
   * ENG-10730. This used to open with `if (this.held.size === 0) return null;` -
   * it returned the affirmative all-clear WITHOUT LOOKING, and the docblock
   * defended that as "a healthy host pays no file read per heartbeat". The
   * saving was real and so was the hole: a host whose `~/.claude.json` had been
   * unlinked, and which had not happened to attempt a rotation since, reported
   * `null` every 30 seconds while already doomed. Nothing asked until some
   * agent's rollover did, which on ENG-10724 was the maintenance window - i.e.
   * the moment the agents were lost. The host was discovered one whole host at a
   * time, at whatever hour its window fell.
   *
   * So the heartbeat now always looks, and pays for it with ONE `statSync` -
   * `probeClaudeDeviceStatePresence`, no read, no parse. The original perf
   * objection is answered rather than overridden: what was rejected in PR #6250
   * was a multi-MB synchronous `JSON.parse` on every healthy host's event loop,
   * and that is still not happening here.
   *
   * THE TWO PATHS ARE NOT SYMMETRIC, AND THAT IS DELIBERATE.
   * - Something held: the FULL check, exactly as before. A hold must only clear
   *   on a genuinely usable file, so a present-but-corrupt one cannot read as a
   *   recovery and release a rotation onto a host that still cannot respawn.
   * - Nothing held: the CHEAP probe. It can only report that the file has GONE,
   *   never that one is well - so it can raise this warning and it can clear
   *   this warning, but it is never allowed to clear a hold.
   *
   * `deferred_agents: []` with a reason is therefore a THIRD state, distinct
   * from both `null` and a hold: the file is unusable and nothing has been
   * stopped yet. The API must not describe that as a hold - see
   * `summariseDeviceStateBlock`, which separates the two so the alert does not
   * claim the manager is holding work it is not.
   */
  heartbeatField(nowMs: number = Date.now()): ClaudeDeviceStateRotationBlocked | null {
    if (this.held.size > 0) {
      const verdict = this.check();
      if (verdict.usable) {
        this.held.clear();
        this.unusableSinceMs = null;
        return null;
      }
      return {
        reason: verdict.reason,
        deferred_agents: [...this.held.keys()].sort(),
        blocked_since: new Date(Math.min(...this.held.values())).toISOString(),
      };
    }
    const presence = this.probe();
    if (presence.present) {
      this.unusableSinceMs = null;
      return null;
    }
    // First heartbeat of this episode: stamp it, so the alert can say how long
    // the host has been in this state rather than resetting its clock every 30s.
    if (this.unusableSinceMs === null) this.unusableSinceMs = nowMs;
    return {
      reason: presence.reason,
      deferred_agents: [],
      blocked_since: new Date(this.unusableSinceMs).toISOString(),
    };
  }

  /** For tests and logs. */
  heldAgents(): string[] {
    return [...this.held.keys()].sort();
  }
}
